import { AxiosResponse } from 'axios';
import { API_ROUTES } from 'data/ApiRoutes';
import { DELETE, GET, PATCH, POST } from 'data/services/api';
import { IPager } from '@data/models/common.model';
import { buildApiPath } from 'utils';

export type InboxStatus = 'open' | 'closed' | 'snoozed';

// One row = the conversation with one family. Intercom time-boxes a thread
// into several `conversations` rows; those are segments of this, and only the
// mutations care about them.
export interface InboxConversation {
  segmentIds: string[];
  segmentCount: number;
  openSegmentCount: number;
  escalatedSegmentCount: number;
  // Segments hold different assignees — an artifact of the time-boxing. The
  // assignee shown is the live segment's; assigning writes all of them.
  splitAssignment: boolean;
  memberUserId: string;
  memberFirstName?: string;
  memberLastName?: string;
  memberName: string;
  companyId?: string;
  companyName?: string;
  parentCompanyId?: string;
  parentCompanyName?: string;
  lastMessageAt?: number;
  lastMessagePreview?: string;
  lastMessageAuthorType?: string;
  inboxStatus: InboxStatus;
  // ISO; set only while the family is snoozed.
  snoozedUntil?: string | null;
  assignedToUserId?: string;
  assignedToName?: string;
  escalatedAt?: string;
  escalatedByName?: string;
  escalationReason?: string;
  syncedAt?: string;
}

export interface InboxAssignee {
  userId: string;
  name: string;
}

export interface InboxCounts {
  open: number;
  snoozed: number;
  unassigned: number;
  mine: number;
  escalated: number;
}

export interface InboxEscalation {
  memberUserId: string;
  escalated: boolean;
  segmentsUpdated: number;
}

export interface IInboxListRequest {
  page: number;
  pageSize: number;
  order?: string;
  status?: string;
  assignedTo?: string;
  escalated?: string;
  query?: string;
  memberUserId?: string;
}

// null while the backend has no intercom_sync_status table; the banner hides.
export interface IntercomSyncSummary {
  // Last clean batch or webhook run. An empty nightly batch stamps it too, so
  // it is not a health signal.
  lastSuccessAt?: string | null;
  // Last Intercom webhook receipt: the signal that Intercom is delivering.
  lastReceivedAt?: string | null;
}

export interface InboxConversationsResponse {
  conversations: InboxConversation[];
  pager: IPager;
  intercomSync: IntercomSyncSummary | null;
}

export interface InboxMemberUpdateResponse {
  memberUserId: string;
  inboxStatus: InboxStatus;
  snoozedUntil?: string | null;
  assignedToUserId: string | null;
  assignedToName: string | null;
  segmentsUpdated: number;
}

export interface InboxMemberUpdateRequest {
  assignedToUserId?: string | null;
  status?: InboxStatus;
  // ISO; required with status 'snoozed'. The backend refuses a time in the
  // past or more than 90 days out.
  snoozedUntil?: string;
}

// One open/snoozed/closed transition of a member family, oldest first.
export interface ConversationStatusEvent {
  conversationStatusEventId: string;
  fromStatus: InboxStatus | null;
  toStatus: InboxStatus;
  snoozedUntil: string | null;
  // 'system' is the snooze timer running out.
  actorType: 'staff' | 'contact' | 'member' | 'system';
  actorUserId: string | null;
  actorName: string | null;
  createdDate: string;
}

const rethrow = (e: unknown): never => {
  if (typeof e === 'string') {
    throw Error(e.toUpperCase());
  } else if (e instanceof Error) {
    throw Error(e.message);
  }
  throw Error('');
};

export const getInboxConversations = async (
  params: IInboxListRequest,
): Promise<InboxConversationsResponse> => {
  try {
    const res: AxiosResponse<InboxConversationsResponse> = await GET(
      buildApiPath({ path: API_ROUTES.inboxConversations, params }),
    );
    return res.data;
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const updateMemberConversation = async (
  memberUserId: string,
  body: InboxMemberUpdateRequest,
): Promise<InboxMemberUpdateResponse> => {
  try {
    const res: AxiosResponse<InboxMemberUpdateResponse> = await PATCH(
      API_ROUTES.inboxMemberConversation(memberUserId),
      body,
    );
    return res.data;
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const getConversationStatusHistory = async (
  memberUserId: string,
): Promise<ConversationStatusEvent[]> => {
  try {
    const res: AxiosResponse<{ events: ConversationStatusEvent[] }> = await GET(
      API_ROUTES.inboxMemberStatusHistory(memberUserId),
    );
    return res.data?.events || [];
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const getInboxAssignees = async (): Promise<InboxAssignee[]> => {
  try {
    const res: AxiosResponse<{ assignees: InboxAssignee[] }> = await GET(
      API_ROUTES.inboxAssignees,
    );
    return res.data?.assignees || [];
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const getInboxCounts = async (): Promise<InboxCounts> => {
  try {
    const res: AxiosResponse<InboxCounts> = await GET(API_ROUTES.inboxCounts);
    return res.data;
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const escalateConversation = async (
  memberUserId: string,
  reason: string,
): Promise<InboxEscalation> => {
  try {
    const res: AxiosResponse<InboxEscalation> = await POST(
      API_ROUTES.inboxMemberEscalate(memberUserId),
      { reason },
    );
    return res.data;
  } catch (e: unknown) {
    return rethrow(e);
  }
};

export const deescalateConversation = async (
  memberUserId: string,
): Promise<InboxEscalation> => {
  try {
    const res: AxiosResponse<InboxEscalation> = await POST(
      API_ROUTES.inboxMemberDeescalate(memberUserId),
      {},
    );
    return res.data;
  } catch (e: unknown) {
    return rethrow(e);
  }
};

// Removes one AI tag from one message. A tag saved twice carries both ids;
// all of them go, or the tag stays on the message.
export const removeMessageTag = async (
  mentionTagIds: string[],
): Promise<void> => {
  try {
    for (const id of mentionTagIds) {
      await DELETE(API_ROUTES.conversationMentionTag(id));
    }
  } catch (e: unknown) {
    return rethrow(e);
  }
};
